/**
 * Minimal shell-owned toast for the `…` menu's copy/delete confirmations
 * (spec.md § Editor / Toast confirmations: dark pill, bottom-centred, ~1.6s
 * auto-dismiss).
 *
 * NOTE (boundary): work-slices.md § Slice B lists a shared Toast component as
 * Slice B's territory. Slice C's menu actions need a toast and Slice B has not
 * landed, so this is a deliberately small shell-local toast scoped to the
 * shell's copy/delete confirmations. At integration it can be replaced by Slice
 * B's shared Toast. Flagged in the PR "Assumptions" section.
 */

import { useCallback, useEffect, useRef, useState } from "react";

const AUTO_DISMISS_MS = 1600;

export interface ToastController {
  show: (message: string) => void;
}

export function useToast(): {
  controller: ToastController;
  element: React.JSX.Element;
} {
  const [message, setMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((next: string) => {
    setMessage(next);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setMessage(null);
    }, AUTO_DISMISS_MS);
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const element = (
    <div
      // Polite live region so screen readers announce confirmations.
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center"
    >
      {message ? (
        <div
          role="status"
          className="rounded-full bg-primary px-4 py-2 font-mono text-[12.5px] text-primary-fg shadow-lg"
        >
          {message}
        </div>
      ) : null}
    </div>
  );

  return { controller: { show }, element };
}
